'use client'

import { useId } from 'react'

import type { PetAccessory, PetColor, PetMood, PetScene, PetSpecies, PetStage } from '@/lib/dashboard'

/**
 * A page's pet, drawn.
 *
 * Built in layers rather than as one picture per pet: a body for the species,
 * a face for the mood, a size for the stage, a palette for the colour, and
 * accessories laid over the top. A handful of drawings makes every combination,
 * and the same pet keeps looking like itself as its mood changes, because only
 * the face swaps.
 *
 * Every shape shares one 120-unit box with its feet on the same line and its
 * eyes in the same place, so a face drawn once fits every species and a crown
 * sits on every head.
 *
 * The motion is CSS (the `pet-*` classes in globals.css), one loop per part:
 * a hop, a breath, a blink, a sway. It stops for anybody who has asked their
 * system for less motion.
 */

export interface PetLookInput {
  species: PetSpecies
  color: PetColor
  accessories: PetAccessory[]
  scene: PetScene | null
}

interface Palette {
  body: string
  belly: string
  shade: string
  cheek: string
}

const PALETTES: Record<PetColor, Palette> = {
  peach: { body: '#ffb38a', belly: '#ffd9c2', shade: '#f29a6b', cheek: '#ff7f7f' },
  mint: { body: '#8fdcc0', belly: '#d6f5e9', shade: '#6cc9a6', cheek: '#ff8fa3' },
  lilac: { body: '#c9b8f5', belly: '#ebe3fd', shade: '#ab97e6', cheek: '#ff8fb8' },
  gold: { body: '#f2c14e', belly: '#fbe3a1', shade: '#d9a630', cheek: '#ff8a65' },
  // The body takes a gradient instead (see `fill`); these are what it sits on.
  holo: { body: '#c9b8f5', belly: '#f4f0ff', shade: '#b9a8f0', cheek: '#ff8fb8' },
}

/** The colour of a pet nobody has looked after. */
const SICK: Palette = { body: '#c9c6a8', belly: '#e0dec7', shade: '#a9a68c', cheek: '#c9c6a8' }

const INK = '#2b1d16'

/** Halfway to the sick colour: a grown-up that was not looked after. */
function muted(palette: Palette): Palette {
  const mix = (a: string, b: string) => {
    const channel = (hex: string, at: number) => parseInt(hex.slice(at, at + 2), 16)
    const out = [1, 3, 5].map(at => Math.round(channel(a, at) * 0.5 + channel(b, at) * 0.5).toString(16).padStart(2, '0'))
    return `#${out.join('')}`
  }
  return { body: mix(palette.body, SICK.body), belly: mix(palette.belly, SICK.belly), shade: mix(palette.shade, SICK.shade), cheek: palette.cheek }
}

/** How big each stage stands, from its feet. */
const STAGE_SCALE: Record<Exclude<PetStage, 'egg'>, number> = { baby: 0.78, teen: 0.9, adult: 1 }

export function PetSprite({ look, mood, stage, temper = null, className = '', label }: {
  look: PetLookInput
  mood: PetMood
  stage: PetStage
  temper?: 'loved' | 'grumpy' | null
  className?: string
  /** What a screen reader hears. The card's words already say the rest. */
  label: string
}) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, '')
  const sick = mood === 'sick'
  const grumpy = temper === 'grumpy'
  const base = PALETTES[look.color] ?? PALETTES.peach
  const palette = sick ? SICK : grumpy ? muted(base) : base
  const holo = look.color === 'holo' && !sick
  const bodyFill = holo ? `url(#${id}holo)` : palette.body
  const sparkly = (look.color === 'gold' || look.color === 'holo') && !sick

  if(stage === 'egg') {
    return (
      <svg viewBox="-14 -18 148 140" className={className} role="img" aria-label={label}>
        <ellipse cx="60" cy="112" rx="24" ry="3.5" fill="#000" opacity="0.3" />
        <g className="pet-wobble">
          <path d="M60 30 C77 30 89 58 89 76 C89 94 76 104 60 104 C44 104 31 94 31 76 C31 58 43 30 60 30 Z" fill="#f4ead8" />
          <circle cx="49" cy="64" r="5" fill={base.body} />
          <circle cx="70" cy="80" r="7" fill={base.body} />
          <circle cx="55" cy="90" r="3.5" fill={base.body} />
          <path d="M47 52 L53 57 L58 51 L64 57" stroke="#8a7a66" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </svg>
    )
  }

  const scale = STAGE_SCALE[stage]
  const crown = look.accessories.includes('crown')
  const scarf = look.accessories.includes('scarf')
  // Each mood moves the whole pet its own way; parts add their own on top.
  const motion = sick ? 'pet-shiver' : mood === 'happy' ? 'pet-hop' : ''
  const breath = mood === 'sleepy' ? 'pet-breathe pet-slow' : 'pet-breathe'
  const floats = (look.species === 'dragon' || look.species === 'cloud') && mood !== 'sleepy' && !sick

  return (
    <svg viewBox="-14 -18 148 140" className={className} role="img" aria-label={label}>
      { holo && (
        <defs>
          <linearGradient id={`${id}holo`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffb8e0" />
            <stop offset="0.5" stopColor="#b8d4ff" />
            <stop offset="1" stopColor="#b8ffe4" />
          </linearGradient>
        </defs>
      ) }

      <ellipse className={mood === 'happy' && !sick ? 'pet-shadow' : ''} cx="60" cy="112" rx={30 * scale + 4} ry="4" fill="#000" opacity="0.3" />

      <g transform={`translate(60 110) scale(${scale}) translate(-60 -110)`}>
        <g className={`${motion} ${floats ? 'pet-float' : ''}`}>
          <Behind species={look.species} palette={palette} bodyFill={bodyFill} />
          <g className={breath}>
            <g className={holo ? 'pet-holo' : ''}>
              <Body species={look.species} palette={palette} bodyFill={bodyFill} />
            </g>
            <Markings species={look.species} palette={palette} stage={stage} temper={temper} sick={sick} crowned={crown} />
            <Face mood={mood} grumpy={grumpy} palette={palette} />
            { scarf && (
              <g>
                <path d="M22 86 Q60 102 98 86 L97 95 Q60 111 23 95 Z" fill="#e8505b" />
                <path d="M76 99 L88 117 L74 114 Z" fill="#c93d48" />
              </g>
            ) }
            { crown && (
              <g className="pet-crown">
                <path d="M44 37 L46 17 L54 27 L60 12 L66 27 L74 17 L76 37 Z" fill="#ffd166" stroke="#d9a630" strokeWidth="1.5" strokeLinejoin="round" />
                <circle cx="60" cy="30" r="3" fill="#e8505b" />
                <circle cx="50" cy="32" r="2" fill="#7cc6ff" />
                <circle cx="70" cy="32" r="2" fill="#7cc6ff" />
              </g>
            ) }
          </g>
          <Feet species={look.species} palette={palette} />
        </g>
      </g>

      { mood === 'sleepy' && (
        <>
          <text className="pet-z" x="94" y="34" fontSize="14" fontWeight="800" fill="currentColor" opacity="0.6">z</text>
          <text className="pet-z pet-late" x="104" y="22" fontSize="10" fontWeight="800" fill="currentColor" opacity="0.6">z</text>
        </>
      ) }
      { mood === 'hungry' && <path className="pet-drip" d="M94 44 Q97 50 94 53 Q91 50 94 44 Z" fill="#7cc6ff" /> }
      { sparkly && (
        <>
          <path className="pet-twinkle" d="M4 30 L5.5 34.5 L10 36 L5.5 37.5 L4 42 L2.5 37.5 L-2 36 L2.5 34.5 Z" fill="#ffe8a3" />
          <path className="pet-twinkle pet-late" d="M112 70 L113 73 L116 74 L113 75 L112 78 L111 75 L108 74 L111 73 Z" fill="#ffffff" />
        </>
      ) }
    </svg>
  )
}

/** What sits behind the body: a cat's tail, a bunny's ears, a dragon's wings. */
function Behind({ species, palette, bodyFill }: { species: PetSpecies, palette: Palette, bodyFill: string }) {
  if(species === 'cat') {
    return <g className="pet-tail"><path d="M88 98 C110 98 118 80 111 62 C108 55 101 56 102 63 C107 76 103 88 88 90 Z" fill={palette.shade} /></g>
  }
  if(species === 'bunny') {
    return (
      <g className="pet-ears">
        <ellipse cx="44" cy="22" rx="10" ry="25" fill={bodyFill} transform="rotate(-10 44 22)" />
        <ellipse cx="44" cy="24" rx="5" ry="17" fill="#f7b6c8" transform="rotate(-10 44 24)" />
        <ellipse cx="76" cy="22" rx="10" ry="25" fill={bodyFill} transform="rotate(10 76 22)" />
        <ellipse cx="76" cy="24" rx="5" ry="17" fill="#f7b6c8" transform="rotate(10 76 24)" />
      </g>
    )
  }
  if(species === 'dragon') {
    return (
      <>
        <g className="pet-flap"><path d="M30 58 C12 42 -2 46 -4 60 C6 58 12 66 12 74 C20 68 26 68 32 72 Z" fill={palette.shade} /></g>
        <g className="pet-flap-r"><path d="M90 58 C108 42 122 46 124 60 C114 58 108 66 108 74 C100 68 94 68 88 72 Z" fill={palette.shade} /></g>
        <path d="M88 96 Q108 100 106 86 Q100 94 86 90 Z" fill={palette.shade} />
      </>
    )
  }
  return null
}

function Body({ species, palette, bodyFill }: { species: PetSpecies, palette: Palette, bodyFill: string }) {
  if(species === 'cat') {
    return (
      <>
        <path d="M28 52 L32 20 L54 38 Z" fill={bodyFill} />
        <path d="M92 52 L88 20 L66 38 Z" fill={bodyFill} />
        <path d="M33 43 L35 29 L47 38 Z" fill="#f7b6c8" />
        <path d="M87 43 L85 29 L73 38 Z" fill="#f7b6c8" />
        <ellipse cx="60" cy="72" rx="40" ry="35" fill={bodyFill} />
        <ellipse cx="60" cy="84" rx="22" ry="15" fill={palette.belly} />
      </>
    )
  }
  if(species === 'dragon') {
    return (
      <>
        <path d="M42 38 L45 22 L50 36 Z" fill="#f4ead8" />
        <path d="M78 38 L75 22 L70 36 Z" fill="#f4ead8" />
        <ellipse cx="60" cy="71" rx="39" ry="36" fill={bodyFill} />
        <ellipse cx="60" cy="82" rx="22" ry="17" fill={palette.belly} />
      </>
    )
  }
  if(species === 'cloud') {
    return (
      <>
        <path d="M29 46 L35 22 L49 40 Z" fill={bodyFill} />
        <path d="M91 46 L85 22 L71 40 Z" fill={bodyFill} />
        <path d="M33 41 L36 30 L43 38 Z" fill="#f7b6c8" />
        <path d="M87 41 L84 30 L77 38 Z" fill="#f7b6c8" />
        <circle cx="35" cy="80" r="24" fill={bodyFill} />
        <circle cx="85" cy="80" r="24" fill={bodyFill} />
        <circle cx="60" cy="64" r="31" fill={bodyFill} />
        <circle cx="60" cy="88" r="22" fill={bodyFill} />
        <circle cx="46" cy="95" r="13" fill={palette.belly} />
        <circle cx="74" cy="95" r="13" fill={palette.belly} />
      </>
    )
  }
  // Mochi and the bunny share the round body; the bunny's difference is its ears.
  return (
    <>
      <ellipse cx="60" cy="70" rx={species === 'bunny' ? 39 : 42} ry={species === 'bunny' ? 35 : 37} fill={bodyFill} />
      <ellipse cx="60" cy="81" rx="24" ry="17" fill={palette.belly} />
    </>
  )
}

/**
 * What changes as it grows: Mochi's sprout becomes two leaves and then a
 * flower, and a grown-up that was not looked after gets tufts and a wilted leaf.
 * A crown takes the sprout's place on the head.
 */
function Markings({ species, palette, stage, temper, sick, crowned }: {
  species: PetSpecies
  palette: Palette
  stage: Exclude<PetStage, 'egg'>
  temper: 'loved' | 'grumpy' | null
  sick: boolean
  crowned: boolean
}) {
  const withered = sick || temper === 'grumpy'
  return (
    <>
      { species === 'mochi' && !crowned && (
        withered ? (
          <path d="M60 34 C58 28 52 26 47 28 C51 32 56 34 60 34 Z" fill="#a8935f" />
        ) : (
          <g className="pet-sway">
            <path d="M60 34 C60 25 66 18 75 18 C73 27 67 32 60 34 Z" fill="#7ed99b" />
            { stage !== 'baby' && <path d="M60 34 C58 26 50 20 42 21 C45 29 52 33 60 34 Z" fill="#5fc282" /> }
            { stage === 'adult' && temper === 'loved' && <circle cx="60" cy="31" r="5" fill="#ffd166" /> }
          </g>
        )
      ) }
      { species === 'mochi' && stage !== 'baby' && (
        <>
          <ellipse cx="22" cy="62" rx="7" ry="11" fill={palette.shade} transform="rotate(-25 22 62)" />
          <g className={sick ? '' : 'pet-wave'}>
            <ellipse cx="98" cy="62" rx="7" ry="11" fill={palette.shade} transform="rotate(25 98 62)" />
          </g>
        </>
      ) }
      { species === 'cat' && (
        <>
          <path d="M53 44 L55 52 M60 42 L60 51 M67 44 L65 52" stroke={palette.shade} strokeWidth="3" strokeLinecap="round" />
          <path d="M24 72 L33 73 M25 80 L33 80 M96 72 L87 73 M95 80 L87 80" stroke={palette.shade} strokeWidth="3" strokeLinecap="round" />
        </>
      ) }
      { (species === 'cat' || species === 'cloud') && (
        <path d="M22 68 L40 71 M22 76 L40 75 M98 68 L80 71 M98 76 L80 75" stroke={INK} strokeOpacity="0.45" strokeWidth="1.4" strokeLinecap="round" />
      ) }
      { temper === 'grumpy' && !sick && (
        <path d="M46 36 L50 30 L53 36 M58 34 L61 28 L64 34 M67 36 L71 30 L74 36" stroke={palette.shade} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      ) }
    </>
  )
}

/** The same face on every species, which is what keeps a mood readable across them. */
function Face({ mood, grumpy, palette }: { mood: PetMood, grumpy: boolean, palette: Palette }) {
  const brows = grumpy && mood !== 'sleepy' && mood !== 'sick'
  return (
    <>
      { mood === 'happy' && (
        <>
          <path d="M42 63 Q47 56 52 63" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M68 63 Q73 56 78 63" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
          <ellipse cx="38" cy="72" rx="5" ry="3" fill={palette.cheek} opacity="0.55" />
          <ellipse cx="82" cy="72" rx="5" ry="3" fill={palette.cheek} opacity="0.55" />
          <path d="M53 72 Q60 81 67 72 Z" fill={INK} />
        </>
      ) }
      { mood === 'hungry' && (
        <>
          <g className="pet-blink">
            <circle cx="47" cy="63" r="4.5" fill={INK} />
            <circle cx="73" cy="63" r="4.5" fill={INK} />
            <circle cx="48.5" cy="61.5" r="1.4" fill="#fff" />
            <circle cx="74.5" cy="61.5" r="1.4" fill="#fff" />
          </g>
          <ellipse cx="60" cy="77" rx="4" ry="5" fill={INK} />
        </>
      ) }
      { mood === 'sleepy' && (
        <>
          <path d="M41 65 Q47 69 53 65" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M67 65 Q73 69 79 65" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
          <ellipse cx="60" cy="77" rx="3" ry="2" fill={INK} />
        </>
      ) }
      { mood === 'bored' && (
        <>
          <g className="pet-blink pet-slow">
            <circle cx="47" cy="64" r="4" fill={INK} />
            <circle cx="73" cy="64" r="4" fill={INK} />
          </g>
          <path d="M41 61 L53 61 M67 61 L79 61" stroke={palette.body} strokeWidth="5" strokeLinecap="round" />
          <path d="M54 77 L66 77" stroke={INK} strokeWidth="3" strokeLinecap="round" />
        </>
      ) }
      { mood === 'sick' && (
        <>
          <path d="M42 60 L52 64" stroke={INK} strokeWidth="3" strokeLinecap="round" />
          <path d="M78 60 L68 64" stroke={INK} strokeWidth="3" strokeLinecap="round" />
          <path d="M51 77 Q55.5 73 60 77 Q64.5 81 69 77" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
        </>
      ) }
      { brows && (
        <path d="M41 55 L52 58 M79 55 L68 58" stroke={INK} strokeWidth="3" strokeLinecap="round" />
      ) }
    </>
  )
}

function Feet({ species, palette }: { species: PetSpecies, palette: Palette }) {
  if(species === 'cloud') return null
  // The cat's and dragon's sit in front of the belly; the rest tuck under.
  const fill = species === 'cat' ? palette.belly : palette.shade
  return (
    <>
      <ellipse cx="45" cy="106" rx="9" ry="5" fill={fill} />
      <ellipse cx="75" cy="106" rx="9" ry="5" fill={fill} />
    </>
  )
}

/**
 * The backdrop a scene puts behind the pet, cut to whatever box it is given.
 * Flat bands rather than gradients, so it reads as a place and not as a glow.
 */
export function PetBackdrop({ scene, className = '' }: { scene: PetScene, className?: string }) {
  return (
    <svg viewBox="0 0 400 240" preserveAspectRatio="xMidYMid slice" className={className} aria-hidden="true">
      { scene === 'night' && (
        <>
          <rect width="400" height="240" fill="#171a3a" />
          <circle className="pet-glow" cx="320" cy="58" r="36" fill="#fff4c9" opacity="0.3" />
          <circle cx="320" cy="58" r="21" fill="#fff4c9" />
          { [[40, 34, 1.6], [96, 72, 1.2], [150, 26, 1.8], [236, 44, 1.2], [270, 110, 1.1], [60, 128, 1.2], [372, 132, 1.5], [200, 84, 1]].map(([x, y, r]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill="#fff" />
          )) }
          <path d="M0 200 Q100 176 200 196 T400 188 L400 240 L0 240 Z" fill="#232a5c" />
        </>
      ) }
      { scene === 'meadow' && (
        <>
          <rect width="400" height="240" fill="#a8dcf7" />
          <circle cx="330" cy="54" r="24" fill="#ffe08a" />
          <ellipse cx="90" cy="60" rx="34" ry="11" fill="#ffffff" opacity="0.85" />
          <ellipse cx="220" cy="38" rx="26" ry="8" fill="#ffffff" opacity="0.7" />
          <path d="M0 186 Q120 150 230 180 T400 168 L400 240 L0 240 Z" fill="#7cc47f" />
          <path d="M0 214 Q140 192 260 210 T400 204 L400 240 L0 240 Z" fill="#5fae6a" />
        </>
      ) }
      { scene === 'sunset' && (
        <>
          <rect width="400" height="240" fill="#ffb48a" />
          <rect y="0" width="400" height="70" fill="#f78e8e" />
          <circle cx="200" cy="176" r="44" fill="#ffde8a" />
          <path d="M0 184 Q110 160 210 184 T400 176 L400 240 L0 240 Z" fill="#c0637a" />
          <path d="M0 214 Q150 196 270 212 T400 206 L400 240 L0 240 Z" fill="#8e4a6b" />
        </>
      ) }
    </svg>
  )
}
